import LessonRoomApi from '@/api/lessonRoom'

export default {
  namespaced: true,
  state: {
    roomData: null,
    participants: [],
    chatMessages: [],
    isLoading: false,
    error: null,
    currentPresentation: null,
    presentations: [],
    roomState: {
      isActive: false,
      currentPage: 1,
      jitsiUrl: '',
      jitsiRoomName: ''
    },

    // Quiz State
    testPacks: [],
    activeQuiz: null,
    quizStats: {
      participant_count: 0,
      completed_submissions: 0,
      average_score: 0,
      duration: null
    },
    quizResults: null,
    isQuizLoading: false
  },

  mutations: {
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    SET_ROOM_DATA(state, data) {
      state.roomData = data
      state.currentPresentation = data.current_presentation
      state.presentations = data.presentations || []
      state.roomState = data.room_state || state.roomState
    },

    SET_PARTICIPANTS(state, participants) {
      state.participants = participants
    },

    ADD_PARTICIPANT(state, participant) {
      const existingIndex = state.participants.findIndex(p => p.id === participant.id)
      if (existingIndex === -1) {
        state.participants.push(participant)
      } else {
        // Update existing participant
        state.participants.splice(existingIndex, 1, participant)
      }
    },

    REMOVE_PARTICIPANT(state, participantId) {
      state.participants = state.participants.filter(p => p.id !== participantId)
    },

    SET_CHAT_MESSAGES(state, messages) {
      state.chatMessages = messages
    },

    ADD_CHAT_MESSAGE(state, message) {
      state.chatMessages.push(message)
    },

    SET_CURRENT_PRESENTATION(state, presentation) {
      state.currentPresentation = presentation
    },

    ADD_PRESENTATION(state, presentation) {
      state.presentations.unshift(presentation)
    },

    SET_PRESENTATIONS(state, presentations) {
      state.presentations = presentations
    },

    UPDATE_ROOM_STATE(state, roomState) {
      state.roomState = { ...state.roomState, ...roomState }
    },

    UPDATE_CURRENT_PAGE(state, page) {
      state.roomState.currentPage = page
    },

    CLEAR_ROOM_DATA(state) {
      state.roomData = null
      state.participants = []
      state.chatMessages = []
      state.currentPresentation = null
      state.presentations = []
      state.roomState = {
        isActive: false,
        currentPage: 1,
        jitsiUrl: '',
        jitsiRoomName: ''
      }
      state.error = null

      // Clear quiz data
      state.testPacks = []
      state.activeQuiz = null
      state.quizStats = {
        participant_count: 0,
        completed_submissions: 0,
        average_score: 0,
        duration: null
      }
      state.quizResults = null
      state.isQuizLoading = false
    },

    // Quiz Mutations
    SET_QUIZ_LOADING(state, isLoading) {
      state.isQuizLoading = isLoading
    },

    SET_TEST_PACKS(state, testPacks) {
      state.testPacks = testPacks
    },

    SET_ACTIVE_QUIZ(state, quiz) {
      state.activeQuiz = quiz
    },

    UPDATE_ACTIVE_QUIZ(state, updates) {
      if (state.activeQuiz) {
        state.activeQuiz = { ...state.activeQuiz, ...updates }
      }
    },

    SET_QUIZ_STATS(state, stats) {
      state.quizStats = { ...state.quizStats, ...stats }
    },

    UPDATE_QUIZ_STATS(state, updates) {
      state.quizStats = { ...state.quizStats, ...updates }
    },

    SET_QUIZ_RESULTS(state, results) {
      state.quizResults = results
    },

    CLEAR_QUIZ_DATA(state) {
      state.activeQuiz = null
      state.quizStats = {
        participant_count: 0,
        completed_submissions: 0,
        average_score: 0,
        duration: null
      }
      state.quizResults = null
    }
  },

  actions: {
    async loadRoomData({ commit }, webinarId) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      try {
        const response = await LessonRoomApi.getRoomData(webinarId)
        commit('SET_ROOM_DATA', response.data)
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to load room data'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async joinRoom({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.joinRoom(webinarId)
        commit('UPDATE_ROOM_STATE', response.data.room_state)
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to join room'
        commit('SET_ERROR', errorMessage)
        throw error
      }
    },

    async leaveRoom({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.leaveRoom(webinarId)
        commit('CLEAR_ROOM_DATA')
        return response.data
      } catch (error) {
        console.error('Failed to leave room:', error)
        // Don't throw error for leave room as user should be able to navigate away
        commit('CLEAR_ROOM_DATA')
      }
    },

    async startConference({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.startConference(webinarId)
        commit('UPDATE_ROOM_STATE', { isActive: true })
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to start conference'
        throw new Error(errorMessage)
      }
    },

    async stopConference({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.stopConference(webinarId)
        commit('UPDATE_ROOM_STATE', { isActive: false })
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to stop conference'
        throw new Error(errorMessage)
      }
    },

    async finishLesson({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.finishLesson(webinarId)
        commit('CLEAR_ROOM_DATA')
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to finish lesson'
        throw new Error(errorMessage)
      }
    },

    async uploadPresentation({ commit }, { formData, onProgress }) {
      try {
        const response = await LessonRoomApi.uploadPresentation(formData, { onProgress })
        commit('ADD_PRESENTATION', response.data.presentation)
        commit('SET_CURRENT_PRESENTATION', response.data.presentation)
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to upload presentation'
        throw new Error(errorMessage)
      }
    },

    async removeCurrentPresentation({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.removePresentation(webinarId)
        commit('SET_CURRENT_PRESENTATION', null)
        commit('SET_PRESENTATIONS', [])
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to remove presentation'
        throw new Error(errorMessage)
      }
    },

    async changePresentationPage({ commit }, { webinarId, page }) {
      try {
        const response = await LessonRoomApi.changePage(webinarId, { page })
        commit('UPDATE_CURRENT_PAGE', page)
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to change page'
        throw new Error(errorMessage)
      }
    },

    async sendMessage({ commit }, { webinarId, message }) {
      try {
        const response = await LessonRoomApi.sendMessage(webinarId, { message })
        // Don't add to local state here - will be received via WebSocket
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to send message'
        throw new Error(errorMessage)
      }
    },

    async loadChatMessages({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.getChatMessages(webinarId)
        commit('SET_CHAT_MESSAGES', response.data.messages || [])
        return response.data
      } catch (error) {
        console.error('Failed to load chat messages:', error)
        // Don't throw error for chat messages as room should still function
      }
    },

    // Real-time event handlers (called from components when WebSocket events received)
    handleNewMessage({ commit }, message) {
      commit('ADD_CHAT_MESSAGE', message)
    },

    handleParticipantJoined({ commit }, participant) {
      commit('ADD_PARTICIPANT', participant)
    },

    handleParticipantLeft({ commit }, participantId) {
      commit('REMOVE_PARTICIPANT', participantId)
    },

    handlePresentationChanged({ commit }, { presentation, page }) {
      if (presentation) {
        commit('SET_CURRENT_PRESENTATION', presentation)
      }
      if (page) {
        commit('UPDATE_CURRENT_PAGE', page)
      }
    },

    // Utility actions
    clearError({ commit }) {
      commit('SET_ERROR', null)
    },

    clearRoomData({ commit }) {
      commit('CLEAR_ROOM_DATA')
    },

    // Quiz Actions
    async loadTestPacks({ commit }, webinarId) {
      commit('SET_QUIZ_LOADING', true)
      try {
        const response = await LessonRoomApi.getAvailableTestPacks(webinarId)
        commit('SET_TEST_PACKS', response.data.testPacks || [])
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to load test packs'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_QUIZ_LOADING', false)
      }
    },

    async loadActiveQuiz({ commit }, webinarId) {
      try {
        const response = await LessonRoomApi.getActiveQuiz(webinarId)
        commit('SET_ACTIVE_QUIZ', response.data.quiz)
        if (response.data.stats) {
          commit('SET_QUIZ_STATS', response.data.stats)
        }
        return response.data
      } catch (error) {
        // Don't show error for no active quiz - this is normal
        if (error.response?.status !== 404) {
          console.error('Failed to load active quiz:', error)
        }
      }
    },

    async createQuiz({ commit }, { webinarId, ...quizData }) {
      commit('SET_QUIZ_LOADING', true)
      try {
        const response = await LessonRoomApi.createQuiz(webinarId, quizData)
        commit('SET_ACTIVE_QUIZ', response.data.quiz)
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to create quiz'
        throw new Error(errorMessage)
      } finally {
        commit('SET_QUIZ_LOADING', false)
      }
    },

    async startQuiz({ commit }, { webinarId, quizId }) {
      commit('SET_QUIZ_LOADING', true)
      try {
        const response = await LessonRoomApi.startQuiz(webinarId, quizId)
        commit('UPDATE_ACTIVE_QUIZ', { status: 'active' })
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to start quiz'
        throw new Error(errorMessage)
      } finally {
        commit('SET_QUIZ_LOADING', false)
      }
    },

    async endQuiz({ commit }, { webinarId, quizId }) {
      commit('SET_QUIZ_LOADING', true)
      try {
        const response = await LessonRoomApi.endQuiz(webinarId, quizId)
        commit('UPDATE_ACTIVE_QUIZ', { status: 'ended' })
        if (response.data.stats) {
          commit('SET_QUIZ_STATS', response.data.stats)
        }
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to end quiz'
        throw new Error(errorMessage)
      } finally {
        commit('SET_QUIZ_LOADING', false)
      }
    },

    async loadQuizResults({ commit }, { webinarId, quizId }) {
      commit('SET_QUIZ_LOADING', true)
      try {
        const response = await LessonRoomApi.getQuizResults(webinarId, quizId)
        commit('SET_QUIZ_RESULTS', response.data)
        return response.data
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to load quiz results'
        throw new Error(errorMessage)
      } finally {
        commit('SET_QUIZ_LOADING', false)
      }
    },

    // Quiz event handlers (called from components when WebSocket events received)
    handleQuizStarted({ commit }, quiz) {
      commit('SET_ACTIVE_QUIZ', quiz)
    },

    handleQuizEnded({ commit }, { quiz, stats }) {
      commit('UPDATE_ACTIVE_QUIZ', { status: 'ended' })
      if (stats) {
        commit('SET_QUIZ_STATS', stats)
      }
    },

    handleQuizSubmission({ commit }, submission) {
      commit('UPDATE_QUIZ_STATS', {
        completed_submissions: (state.quizStats.completed_submissions || 0) + 1
      })
    },

    clearQuizData({ commit }) {
      commit('CLEAR_QUIZ_DATA')
    }
  },

  getters: {
    isLoading: state => state.isLoading,
    error: state => state.error,
    roomData: state => state.roomData,
    participants: state => state.participants,
    chatMessages: state => state.chatMessages,
    currentPresentation: state => state.currentPresentation,
    presentations: state => state.presentations,
    roomState: state => state.roomState,
    isRoomActive: state => state.roomState.isActive,
    currentPage: state => state.roomState.currentPage,
    jitsiUrl: state => state.roomState.jitsiUrl,

    // Computed getters
    participantCount: state => state.participants.length,
    messageCount: state => state.chatMessages.length,

    hasCurrentPresentation: state => state.currentPresentation !== null,
    totalPages: state => state.currentPresentation?.total_pages || 0,

    canGoToPreviousPage: state => state.roomState.currentPage > 1,
    canGoToNextPage: state => {
      const totalPages = state.currentPresentation?.total_pages || 0
      return state.roomState.currentPage < totalPages
    },

    // Filter participants by role
    teachers: state => state.participants.filter(p => p.role === 'teacher'),
    students: state => state.participants.filter(p => p.role === 'student'),

    // Quiz getters
    isQuizLoading: state => state.isQuizLoading,
    testPacks: state => state.testPacks,
    activeQuiz: state => state.activeQuiz,
    quizStats: state => state.quizStats,
    quizResults: state => state.quizResults,

    hasActiveQuiz: state => state.activeQuiz !== null,
    isQuizActive: state => state.activeQuiz?.status === 'active',
    isQuizEnded: state => state.activeQuiz?.status === 'ended',
    canStartQuiz: state => state.activeQuiz?.status === 'created',

    quizParticipantCount: state => state.quizStats.participant_count || 0,
    quizCompletedSubmissions: state => state.quizStats.completed_submissions || 0,
    quizAverageScore: state => state.quizStats.average_score || 0,
    quizDuration: state => state.quizStats.duration,

    hasTestPacks: state => state.testPacks.length > 0
  }
}